<template>
  <v-col cols="12" v-if="permisoVista('sensores', 'r')" :class="{ 'embedded-col': embedded }">
    <v-card class="sensor-config-card" :class="{ 'sensor-config-card--embedded': embedded }" elevation="0">
      <div v-if="!embedded" class="sensor-config-card__glow"></div>

      <div v-if="!embedded" class="sensor-config-card__header">
        <div>
          <div class="sensor-config-card__eyebrow">Configuracion</div>
          <h2 class="sensor-config-card__title">Tokens</h2>
          <p class="sensor-config-card__subtitle">
            Administra los tokens de maquina y su vigencia.
          </p>
        </div>

        <v-btn
          v-if="permisoVista('sensores', 'c')"
          color="primary"
          dark
          class="sensor-config-card__cta"
          @click="openCreateExternal"
        >
          <v-icon left>mdi-plus-circle-outline</v-icon>
          Nuevo Token
        </v-btn>
      </div>

      <v-dialog v-model="dialog" persistent max-width="560px">
        <v-card class="sensor-dialog">
            <v-card-title class="sensor-dialog__title">
              {{ formTitle }}
            </v-card-title>
            <v-card-text>
              <v-container>
                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      v-model.trim="editedItem.token"
                      label="Token"
                      prepend-inner-icon="mdi-key-variant"
                      outlined
                      dense
                      maxlength="100"
                      autofocus
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-select
                      v-model="editedItem.tipoToken"
                      :items="tiposToken"
                      label="Tipo de Token"
                      prepend-inner-icon="mdi-cog-outline"
                      outlined
                      dense
                    ></v-select>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model.trim="editedItem.descripcion"
                      label="Descripcion"
                      prepend-inner-icon="mdi-text-box-outline"
                      outlined
                      dense
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-alert
                  v-if="errores.length > 0"
                  type="error"
                  text
                  dense
                  class="mb-0"
                >
                  <div
                    v-for="(error, index) in errores"
                    :key="'error-token-' + index"
                  >
                    {{ error }}
                  </div>
                </v-alert>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="blue darken-1" text @click="close">
                Cancelar
              </v-btn>
              <v-btn
                color="red darken-1"
                text
                :disabled="saving"
                :loading="saving"
                @click="save"
              >
                Guardar
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

      <div class="sensor-config-card__toolbar">
        <v-text-field
          v-model="search"
          append-icon="search"
          label="Buscar token, tipo o descripcion"
          single-line
          hide-details
          outlined
          dense
          class="sensor-config-card__search"
        ></v-text-field>

        <v-btn text color="primary" @click="initialize">
          <v-icon left small>mdi-refresh</v-icon>
          Refrescar
        </v-btn>
      </div>


      <v-data-table
        class="sensor-config-table"
        :headers="headers"
        :items="tokens"
        :search="search"
        :loading="loading"
        loading-text="Cargando... Espere, Por Favor."
      >
        <template v-slot:[`item.token`]="{ item }">
          <div class="sensor-name-cell">
            <div class="sensor-name-cell__icon">
              <v-icon color="primary" small>mdi-key-variant</v-icon>
            </div>
            <div>
              <div class="sensor-name-cell__title">{{ item.token }}</div>
              <div class="sensor-name-cell__caption">
                Tipo: {{ item.tipoToken || "N/A" }}
              </div>
            </div>
          </div>
        </template>

        <template v-slot:[`item.tipoToken`]="{ item }">
          <v-chip small outlined color="primary" class="sensor-unit-chip">
            {{ item.tipoToken || "N/A" }}
          </v-chip>
        </template>

        <template v-slot:[`item.descripcion`]="{ item }">
          <span class="text--secondary">{{ item.descripcion || "N/A" }}</span>
        </template>

        <template v-slot:[`item.vigencia`]="{ item }">
          <div class="token-date-cell">
            <div>{{ formatDate(item.fechaInicio) || "Sin inicio" }}</div>
            <div class="sensor-name-cell__caption">
              Fin: {{ formatDate(item.fechaFin) || "Sin fecha" }}
            </div>
          </div>
        </template>

        <template v-slot:[`item.maquinasAsociadas`]="{ item }">
          <div class="asoc-cell">
            <span class="asoc-cell__count">
              {{ item.maquinasAsociadas || 0 }} máquina{{ item.maquinasAsociadas === 1 ? '' : 's' }}
            </span>
            <a v-if="item.maquinasAsociadas > 0" class="asoc-cell__link" @click="verAsociaciones(item)">Ver detalle</a>
            <span v-else class="asoc-cell__empty">Sin asignar</span>
          </div>
        </template>

        <template v-slot:[`item.condicion`]="{ item }">
          <v-switch
            :key="'tk-sw-' + item.idToken + '-' + estadoNonce"
            :input-value="item.condicion !== false"
            :loading="togglingId === item.idToken"
            :disabled="togglingId === item.idToken || !(permisoVista('sensores','u') || permisoVista('sensores','d'))"
            color="success"
            dense
            hide-details
            inset
            class="estado-switch"
            :label="item.condicion === false ? 'Inactivo' : 'Activo'"
            @change="toggleEstado(item)"
          ></v-switch>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-tooltip
            v-if="permisoVista('sensores', 'u')"
            bottom
            color="black"
            class="white--text"
          >
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                icon
                small
                color="blue darken-1"
                @click="editItem(item)"
              >
                <v-icon small>mdi-pencil-outline</v-icon>
              </v-btn>
            </template>
            <span class="white--text">Editar</span>
          </v-tooltip>
        </template>

        <template v-slot:no-data>
          <div class="sensor-empty-state">
            <v-icon color="blue-grey lighten-1" large>
              mdi-key-off
            </v-icon>
            <div class="sensor-empty-state__title">No hay tokens registrados</div>
            <div class="sensor-empty-state__subtitle">
              Crea un token nuevo para comenzar.
            </div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialogDependencias" max-width="520px">
      <v-card v-if="dependItem" class="depend-dialog">
        <v-card-title class="depend-dialog__title">
          <v-icon color="warning" left>mdi-alert-outline</v-icon>
          No se puede desactivar el token
        </v-card-title>
        <v-card-text>
          <p class="mb-3">
            El token <strong>{{ dependItem.token }}</strong> no puede desactivarse porque tiene
            <strong>{{ dependItem.maquinasAsociadas }} máquina(s) asociada(s)</strong>.
          </p>
          <div class="subtitle-2 mb-1">Máquinas asociadas:</div>
          <ul class="depend-dialog__list">
            <li v-for="(m, i) in dependItem.maquinasAsociadasLista" :key="i">{{ m }}</li>
          </ul>
          <v-alert dense text type="warning" class="mb-0 mt-3">
            Para desactivar este token, primero libera la máquina asociada.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="dialogDependencias = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      top
      centered
      :timeout="3200"
      :color="snackbar.color"
      class="sensor-snackbar"
    >
      <div class="sensor-snackbar__content">
        <v-icon dark left>{{ snackbar.icon }}</v-icon>
        <span>{{ snackbar.text }}</span>
      </div>
    </v-snackbar>
  </v-col>
</template>

<script>
import axios from "axios";

export default {
  props: {
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      saving: false,
      loadingItem: false,
      dialog: false,
      dialogDependencias: false,
      dependItem: null,
      togglingId: null,
      estadoNonce: 0,
      search: "",
      tokens: [],
      errores: [],
      editedIndex: -1,
      tiposToken: ["MAQUINA"],
      headers: [
        {
          text: "Token",
          value: "token",
          align: "left",
          sortable: true,
        },
        {
          text: "Tipo",
          value: "tipoToken",
          align: "left",
          sortable: true,
        },
        {
          text: "Descripcion",
          value: "descripcion",
          align: "left",
          sortable: true,
        },
        {
          text: "Vigencia",
          value: "vigencia",
          align: "left",
          sortable: false,
        },
        {
          text: "Máquina asociada",
          value: "maquinasAsociadas",
          align: "center",
          sortable: true,
        },
        {
          text: "Estado",
          value: "condicion",
          align: "center",
          sortable: true,
        },
        {
          text: "Acciones",
          value: "actions",
          sortable: false,
          align: "center",
          width: "12%",
        },
      ],
      editedItem: {
        idToken: null,
        token: "",
        tipoToken: "MAQUINA",
        descripcion: "",
        condicion: true,
      },
      defaultItem: {
        idToken: null,
        token: "",
        tipoToken: "MAQUINA",
        descripcion: "",
        condicion: true,
      },
      snackbar: {
        show: false,
        text: "",
        color: "success",
        icon: "mdi-check-circle-outline",
      },
    };
  },
  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "Crear Token" : "Editar Token";
    },
  },
  created() {
    this.initialize();
  },
  methods: {
    initialize() {
      this.setLoading(true);
      axios
        .get("/CatTokens?incluirInactivos=true")
        .then((response) => {
          const items = Array.isArray(response.data) ? response.data : [];
          this.tokens = items.map(this.normalizeItem);
          this.setLoading(false);
          this.$emit("count-changed", this.tokens.length);
        })
        .catch((error) => {
          this.setLoading(false);
          this.handleError(error, "No se pudieron cargar los tokens.");
        });
    },
    setLoading(value) {
      this.loading = value;
      this.$emit("loading-changed", value);
    },
    openCreate() {
      this.editedIndex = -1;
      this.editedItem = Object.assign({}, this.defaultItem);
      this.errores = [];
    },
    openCreateExternal() {
      this.openCreate();
      this.dialog = true;
    },
    normalizeItem(item = {}) {
      return {
        ...item,
        idToken: item.idToken || item.IdToken || item.id || item.Id || null,
        token: item.token || item.Token || "",
        tipoToken: item.tipoToken || item.TipoToken || "MAQUINA",
        descripcion: item.descripcion || item.Descripcion || "",
        condicion: typeof item.condicion === "boolean" ? item.condicion : item.Condicion,
        maquinasAsociadas: item.maquinasAsociadas ?? item.MaquinasAsociadas ?? 0,
        maquinasAsociadasLista: item.maquinasAsociadasLista || item.MaquinasAsociadasLista || [],
        fechaInicio: this.toDateTimeLocal(item.fechaInicio || item.FechaInicio),
        fechaFin: this.toDateTimeLocal(item.fechaFin || item.FechaFin),
        fechaCreacion: item.fechaCreacion || item.FechaCreacion || null,
        fechaModificacion: item.fechaModificacion || item.FechaModificacion || null,
      };
    },
    toDateTimeLocal(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return typeof value === "string" ? value.slice(0, 16) : "";
      }
      const offset = date.getTimezoneOffset();
      const localDate = new Date(date.getTime() - offset * 60000);
      return localDate.toISOString().slice(0, 16);
    },
    formatDate(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleString("es-MX", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    validate() {
      this.errores = [];
      if (!this.editedItem.token) {
        this.errores.push("El token es obligatorio.");
      }
      if (this.editedItem.token && this.editedItem.token.length > 100) {
        this.errores.push("El token no puede exceder 100 caracteres.");
      }
      if (!this.editedItem.tipoToken) {
        this.errores.push("El tipoToken es obligatorio.");
      }
      if (this.editedItem.tipoToken && this.editedItem.tipoToken.length > 30) {
        this.errores.push("El tipoToken no puede exceder 30 caracteres.");
      }
      if (this.editedItem.tipoToken && this.editedItem.tipoToken !== "MAQUINA") {
        this.errores.push("Solo se acepta el tipoToken MAQUINA.");
      }
      const tokenActual = (this.editedItem.token || "").trim().toUpperCase();
      const tipoActual = (this.editedItem.tipoToken || "").trim().toUpperCase();
      const duplicate = this.tokens.find((item) => {
        if (this.editedItem.idToken && item.idToken === this.editedItem.idToken) return false;
        return (
          (item.token || "").trim().toUpperCase() === tokenActual &&
          (item.tipoToken || "").trim().toUpperCase() === tipoActual
        );
      });
      if (duplicate) {
        this.errores.push("La combinación Token + TipoToken ya existe.");
      }

      return this.errores.length === 0;
    },
    save() {
      if (!this.validate()) return;

      this.saving = true;
      const payload = {
        token: this.editedItem.token,
        tipoToken: this.editedItem.tipoToken,
        descripcion: this.editedItem.descripcion || null,
      };

      const request = this.editedIndex === -1
        ? axios.post("/CatTokens/Crear", payload)
        : axios.put(`/CatTokens/Actualizar/${this.editedItem.idToken}`, payload);

      request
        .then(() => {
          this.saving = false;
          this.close();
          this.initialize();
          this.showSnackbar(
            "Token guardado correctamente.",
            "success",
            "mdi-check-circle-outline"
          );
        })
        .catch((error) => {
          this.saving = false;
          this.handleError(error, "No se pudo guardar el token.");
        });
    },
    editItem(item) {
      const idToken = item && item.idToken;
      if (!idToken) {
        this.showSnackbar(
          "No se encontró el identificador del token.",
          "error",
          "mdi-alert-circle-outline"
        );
        return;
      }

      this.loadingItem = true;
      axios
        .get(`/CatTokens/Mostrar/${idToken}`)
        .then((response) => {
          const payload = response && response.data ? response.data : item;
          this.editedIndex = this.tokens.indexOf(item);
          this.editedItem = Object.assign({}, this.normalizeItem(payload));
          this.errores = [];
          this.dialog = true;
          this.loadingItem = false;
        })
        .catch((error) => {
          this.loadingItem = false;
          this.handleError(error, "No se pudo cargar la información del token.");
        });
    },
    toggleEstado(item) {
      const idToken = item && item.idToken;
      if (!idToken) {
        this.showSnackbar(
          "No se encontró el identificador del token.",
          "error",
          "mdi-alert-circle-outline"
        );
        return;
      }

      const activar = item.condicion === false;

      // Si se intenta desactivar y tiene máquinas asociadas, se bloquea y se muestra el detalle.
      if (!activar && item.maquinasAsociadas > 0) {
        this.verAsociaciones(item);
        this.estadoNonce += 1; // revierte el interruptor
        return;
      }

      this.togglingId = idToken;
      axios
        .put(`/CatTokens/${activar ? "Activar" : "Desactivar"}/${idToken}`)
        .then(() => {
          this.initialize();
          this.showSnackbar(
            activar ? "Token activado correctamente." : "Token desactivado correctamente.",
            "success",
            activar ? "mdi-check-circle-outline" : "mdi-delete-outline"
          );
        })
        .catch((error) => {
          const status = error && error.response ? error.response.status : null;
          if (!activar && status === 409) {
            this.verAsociaciones(item);
          } else {
            this.handleError(
              error,
              activar ? "No se pudo activar el token." : "No se pudo desactivar el token."
            );
          }
          this.initialize();
        })
        .finally(() => {
          this.togglingId = null;
          this.estadoNonce += 1;
        });
    },
    verAsociaciones(item) {
      this.dependItem = item;
      this.dialogDependencias = true;
    },
    close() {
      this.dialog = false;
      this.errores = [];
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },
    showSnackbar(text, color = "error", icon = "mdi-alert-circle-outline") {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.icon = icon;
      this.snackbar.show = true;
    },
    handleError(error, fallbackMessage) {
      const responseData = error && error.response ? error.response.data : null;
      let message = fallbackMessage;

      if (typeof responseData === "string") {
        message = responseData;
      } else if (responseData && Array.isArray(responseData.errors)) {
        message = responseData.errors.join(", ");
      } else if (responseData && typeof responseData.title === "string") {
        message = responseData.title;
      }

      if (this.dialog) {
        this.errores = [message];
      }
      this.showSnackbar(message, "error", "mdi-alert-circle-outline");
      console.log(error);
    },
  },
};
</script>

<style scoped>
.sensor-config-card {
  position: relative;
  overflow: hidden;
  border-radius: 22px;
  padding: 24px;
  background:
    radial-gradient(circle at top right, rgba(25, 118, 210, 0.14), transparent 28%),
    linear-gradient(135deg, #ffffff 0%, #f7fafc 100%);
  border: 1px solid rgba(25, 118, 210, 0.10);
}

.sensor-config-card--embedded {
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0;
}

.embedded-col {
  padding: 0;
}

.sensor-config-card__glow {
  position: absolute;
  inset: auto -60px -80px auto;
  width: 220px;
  height: 220px;
  background: radial-gradient(circle, rgba(25, 118, 210, 0.12), transparent 68%);
  pointer-events: none;
}

.sensor-config-card__header {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  margin-bottom: 18px;
}

.sensor-config-card__eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-size: 12px;
  font-weight: 700;
  color: #1976d2;
  margin-bottom: 6px;
}

.sensor-config-card__title {
  margin: 0;
  font-size: 30px;
  line-height: 1.05;
  color: #102a43;
}

.sensor-config-card__subtitle {
  margin: 8px 0 0;
  color: #5c6b7a;
  max-width: 560px;
}

.sensor-config-card__cta {
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(25, 118, 210, 0.22);
}

.sensor-config-card__toolbar {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.sensor-config-card__search {
  max-width: 380px;
}

.sensor-config-table {
  background: transparent;
}

.sensor-name-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0;
}

.sensor-name-cell__icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(25, 118, 210, 0.10);
}

.sensor-name-cell__title {
  font-weight: 700;
  color: #102a43;
}

.sensor-name-cell__caption {
  font-size: 12px;
  color: #7b8794;
}

.sensor-unit-chip {
  font-weight: 600;
}

.estado-switch {
  display: inline-flex;
  margin-top: 0;
  padding-top: 0;
}

.asoc-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.25;
}
.asoc-cell__count {
  font-weight: 600;
  color: #334e68;
}
.asoc-cell__link {
  font-size: 12px;
  color: #1976d2;
  cursor: pointer;
}
.asoc-cell__link:hover {
  text-decoration: underline;
}
.asoc-cell__empty {
  font-size: 12px;
  color: #94a3b8;
}
.depend-dialog__title {
  font-weight: 700;
  color: #b45309;
  word-break: normal;
}
.depend-dialog__list {
  margin: 0;
  padding-left: 18px;
}
.depend-dialog__list li {
  margin-bottom: 2px;
}

.token-date-cell {
  min-width: 190px;
}

.sensor-empty-state {
  padding: 30px 16px;
  text-align: center;
}

.sensor-empty-state__title {
  margin-top: 10px;
  font-weight: 700;
  color: #334e68;
}

.sensor-empty-state__subtitle {
  margin-top: 4px;
  color: #7b8794;
}

.sensor-dialog {
  border-radius: 18px;
}

.sensor-dialog__title {
  color: #102a43;
  font-weight: 700;
}

.sensor-snackbar__content {
  min-width: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-weight: 600;
}

@media (max-width: 960px) {
  .sensor-config-card {
    padding: 18px;
  }

  .sensor-config-card__header,
  .sensor-config-card__toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .sensor-config-card__search {
    max-width: none;
  }
}
</style>
